import { useEffect, useRef } from "react";
import { classNames } from "primereact/utils";
import styles from "./EmployeesSelectionStep.module.scss";
import { EmployeeCard } from "./EmployeeCard/EmployeeCard";
import { IEmployeeAvailability } from "../../models/IEmployee";

interface IEmployeesSelectionStepProps {
  className?: string;
  employees: IEmployeeAvailability[];
  selectedEmployee?: IEmployeeAvailability | null;
  onEmployeeSelect: (employee: IEmployeeAvailability) => void;
  isLoading?: boolean;
  title?: string;
  subtitle?: string;
  loadingText?: string;
  emptyText?: string;
  serviceId?: number;
  studioId?: number;
}

export const EmployeesSelectionStep = ({
  className,
  employees,
  selectedEmployee,
  onEmployeeSelect,
  isLoading = false,
  title = "Изберете специалист",
  subtitle = "Изберете предпочитания специалист",
  loadingText = "Зареждане на специалисти...",
  emptyText = "В момента няма налични специалисти",
  serviceId,
  studioId,
}: IEmployeesSelectionStepProps) => {
  const gridRef = useRef<HTMLDivElement>(null);

  // Equalize card heights within each row
  useEffect(() => {
    const equalizeCardHeights = () => {
      if (!gridRef.current) return;
      
      const cards = Array.from(gridRef.current.children) as HTMLElement[];
      if (cards.length === 0) return;

      // Reset all heights first
      cards.forEach(card => {
        card.style.height = 'auto';
      });

      // Group cards by rows (2 cards per row)
      const cardsPerRow = 2;
      for (let i = 0; i < cards.length; i += cardsPerRow) {
        const rowCards = cards.slice(i, i + cardsPerRow);
        
        // Find the maximum height in this row
        const maxHeight = Math.max(
          ...rowCards.map(card => card.offsetHeight)
        );
        
        // Set all cards in this row to the maximum height
        rowCards.forEach(card => {
          card.style.height = `${maxHeight}px`;
        });
      }
    };

    // Run after layout is complete
    const timeoutId = setTimeout(equalizeCardHeights, 100);
    
    // Re-run when window resizes
    window.addEventListener('resize', equalizeCardHeights);
    
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', equalizeCardHeights);
    };
  }, [employees, selectedEmployee]);
  if (isLoading) {
    return (
      <div className={classNames(styles.container, className)}>
        <div className={styles.header}>
          <h2 className={styles.title}>{title}</h2>
          <p className={styles.subtitle}>
            {subtitle}
          </p>
        </div>
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner}></div>
          <p className={styles.loadingText}>{loadingText}</p>
        </div>
      </div>
    );
  }

  if (!employees || employees.length === 0) {
    return (
      <div className={classNames(styles.container, className)}>
        <div className={styles.header}>
          <h2 className={styles.title}>{title}</h2>
          <p className={styles.subtitle}>
            {subtitle}
          </p>
        </div>
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>👥</div>
          <p className={styles.emptyText}>
            {emptyText}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={classNames(styles.container, className)}>
      {/* Header */}
      <div className={styles.header}>
        <h2 className={styles.title}>{title}</h2>
        <p className={styles.subtitle}>
          {subtitle}
        </p>
      </div>

      {/* Employees Grid */}
      <div className={styles.employeesGrid} ref={gridRef}>
        {employees.map((employee) => (
          <EmployeeCard
            key={employee.id}
            employee={employee}
            isSelected={selectedEmployee?.id === employee.id}
            onClick={() => onEmployeeSelect(employee)}
            serviceId={serviceId}
            studioId={studioId}
          />
        ))}
      </div>
    </div>
  );
};